/**
 * Hooks personalizados para interagir com o backend EduRPG
 */

import { useState, useCallback } from 'react'
import { toast } from 'sonner'
import { apiService, APIError, type User, type Table, type Character } from '@/services/api'

// Hook para autenticação
export function useAuth() {
  const [isLoading, setIsLoading] = useState(false)

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    setIsLoading(true)
    try {
      const response = await apiService.login(email, password)
      toast.success(`Bem-vindo(a), ${response.user.full_name}!`)
      return response.user
    } catch (error) {
      if (error instanceof APIError) {
        if (error.status === 0) {
          toast.error('Não foi possível conectar ao servidor. Verifique se o backend está rodando na porta 5004.')
        } else if (error.status !== 401) {
          // Evita toast para 401; mensagem é exibida inline no formulário
          toast.error(error.message)
        }
      } else {
        toast.error('Erro inesperado ao fazer login')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const register = useCallback(async (userData: {
    username: string
    email: string
    full_name: string
    password: string
    role: 'professor' | 'estudante'
  }): Promise<User> => {
    setIsLoading(true)
    try {
      const user = await apiService.register(userData)
      toast.success('Conta criada com sucesso!')
      return user
    } catch (error) {
      if (error instanceof APIError) {
        if (error.status === 400) {
          toast.error('Dados inválidos. Verifique se o email já não está em uso.')
        } else if (error.status === 0) {
          toast.error('Não foi possível conectar ao servidor. Verifique se o backend está rodando.')
        } else {
          toast.error(error.message)
        }
      } else {
        toast.error('Erro inesperado ao criar conta')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    apiService.logout()
    toast.success('Logout realizado com sucesso')
  }, [])

  return { login, register, logout, isLoading }
}

// Hook para gerenciar mesas/campanhas
export function useTables() {
  const [isLoading, setIsLoading] = useState(false)
  const [tables, setTables] = useState<Table[]>([])

  const loadTables = useCallback(async () => {
    setIsLoading(true)
    try {
      const tablesData = await apiService.getUserTables()
      console.log("Mesas recebidas do backend:", tablesData);
      
      // Log detalhado de cada mesa para depuração
      tablesData.forEach((table, index) => {
        console.log(`Mesa ${index + 1}: ${table.name}`);
        console.log(`- ID: ${table.id}`);
        console.log(`- ID do criador: ${table.creator_id}`);
        console.log(`- Descrição: ${table.description}`);
      });
      
      setTables(tablesData)
    } catch (error) {
      if (error instanceof APIError) {
        if (error.status === 0) {
          toast.error('Erro de conexão com o servidor')
        } else {
          toast.error('Erro ao carregar campanhas')
        }
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])
  
  // Adicionar função para obter detalhes da mesa
  const getTableDetails = useCallback(async (tableId: string) => {
    setIsLoading(true)
    try {
      const details = await apiService.getTableDetails(tableId)
      return details
    } catch (error) {
      if (error instanceof APIError) {
        if (error.status === 404) {
          toast.error('Campanha não encontrada')
        } else if (error.status === 403) {
          toast.error('Você não tem permissão para acessar esta campanha')
        } else {
          toast.error('Erro ao carregar detalhes da campanha')
        }
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createTable = useCallback(async (tableData: {
    name: string
    description: string
    subject: string
    image_url?: string
    template_id?: string
    game_config?: any
  }): Promise<Table> => {
    setIsLoading(true)
    try {
      // Se tiver template_id, usar endpoint /tables/from-template (sem /api no início)
      const endpoint = tableData.template_id 
        ? '/tables/from-template'
        : '/tables'
      
      const newTable = await apiService.createTable(tableData, endpoint)
      setTables(prev => [...prev, newTable])
      toast.success('Campanha criada com sucesso!')
      return newTable
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao criar campanha: ' + error.message)
      } else {
        toast.error('Erro inesperado ao criar campanha')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateTable = useCallback(async (
    tableId: string,
    updates: Partial<{
      name: string
      description: string
      subject: string
      image_url: string
    }>
  ): Promise<Table> => {
    setIsLoading(true)
    try {
      const updatedTable = await apiService.updateTable(tableId, updates)
      setTables(prev => prev.map(t => t.id === tableId ? updatedTable : t))
      toast.success('Campanha atualizada com sucesso!')
      return updatedTable
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao atualizar campanha: ' + error.message)
      } else {
        toast.error('Erro inesperado ao atualizar campanha')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteTable = useCallback(async (tableId: string): Promise<void> => {
    setIsLoading(true)
    try {
      await apiService.deleteTable(tableId)
      setTables(prev => prev.filter(t => t.id !== tableId))
      toast.success('Campanha excluída com sucesso!')
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao excluir campanha: ' + error.message)
      } else {
        toast.error('Erro inesperado ao excluir campanha')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const duplicateTable = useCallback(async (tableId: string): Promise<Table> => {
    setIsLoading(true)
    try {
      const duplicatedTable = await apiService.duplicateTable(tableId)
      setTables(prev => [...prev, duplicatedTable])
      toast.success('Campanha duplicada com sucesso!')
      return duplicatedTable
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao duplicar campanha: ' + error.message)
      } else {
        toast.error('Erro inesperado ao duplicar campanha')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])


  // Atualizar a imagem de uma mesa
  const updateTableImage = useCallback(async (tableId: string, formData: FormData): Promise<string> => {
    setIsLoading(true)
    try {
      const imageUrl = await apiService.updateTableImage(tableId, formData)
      // Atualizar a lista local de mesas (ambos os formatos para garantir compatibilidade)
      setTables(current => 
        current.map(table => 
          table.id === tableId ? { ...table, image_url: imageUrl, imageUrl: imageUrl } : table
        )
      )
      return imageUrl
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao atualizar imagem: ' + error.message)
      } else {
        toast.error('Erro inesperado ao atualizar imagem')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    tables,
    isLoading,
    loadTables,
    createTable,
    updateTable,
    deleteTable,
    duplicateTable,
    getTableDetails,
    updateTableImage
  }
}

// Hook para gerenciar personagens
export function useCharacters() {
  const [isLoading, setIsLoading] = useState(false)

  const createCharacter = useCallback(async (characterData: {
    name: string
    description: string
    class_name: string
    table_id: string
    image_url?: string
    level?: number
    hp_total?: number
    hp_current?: number
    points?: number
    // Atributos dinâmicos
    [key: string]: any
  }): Promise<any> => {
    setIsLoading(true)
    try {
      const newCharacter = await apiService.createCharacter(characterData);
      toast.success('Personagem criado com sucesso!')
      return newCharacter
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao criar personagem: ' + error.message)
      } else {
        toast.error('Erro inesperado ao criar personagem')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateCharacter = useCallback(async (
    characterId: string,
    updates: Partial<{
      name: string
      description: string
      class_name: string
      level: number
      hp_total: number
      hp_current: number
      strength: number
      dexterity: number
      constitution: number
      cognition: number
      perception: number
      charisma: number
      points: number
    }>
  ): Promise<Character> => {
    setIsLoading(true)
    try {
      const updatedCharacter = await apiService.updateCharacter(characterId, updates)
      toast.success('Personagem atualizado com sucesso!')
      return updatedCharacter
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao atualizar personagem: ' + error.message)
      } else {
        toast.error('Erro inesperado ao atualizar personagem')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteCharacter = useCallback(async (characterId: string): Promise<void> => {
    setIsLoading(true)
    try {
      await apiService.deleteCharacter(characterId)
      toast.success('Personagem excluído com sucesso!')
    } catch (error) {
      if (error instanceof APIError) {
        toast.error('Erro ao excluir personagem: ' + error.message)
      } else {
        toast.error('Erro inesperado ao excluir personagem')
      }
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    isLoading,
    createCharacter,
    updateCharacter,
    deleteCharacter
  }
}

// Hook para verificar saúde da API
export function useAPIHealth() {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  const checkHealth = useCallback(async () => {
    setIsChecking(true)
    try {
      await apiService.healthCheck()
      setIsHealthy(true)
    } catch (error) {
      setIsHealthy(false)
    } finally {
      setIsChecking(false)
    }
  }, [])

  return { isHealthy, isChecking, checkHealth }
}
